import React from 'react';
import * as yup from 'yup';
import { useUpdateDataValueTsMutation } from 'Services/api/dataValueApi';
import { Button, Pane, toaster } from 'evergreen-ui';
import CornerDialogError from 'Utils/CornerDialogError';
import { DataValueTSFull } from './types';
import EditForm from './EditData.Form';
import HistoryChanges from './EditData.HistoryChanges';

type MeasurementProps = {
  measurement: DataValueTSFull
  zone: string
  index: number
  onChangeValue: (index: number, value: DataValueTSFull) => void
  isSeries?:boolean
  existingPoints?: number[]
}

const validationSchema = (isSeries: boolean) => {
  const defaultValidation: {
    value: yup.NumberSchema<number, yup.AnyObject, undefined, ''>,
    point?: yup.NumberSchema<number, yup.AnyObject, undefined, ''>,
  } = {
    value: yup
      .number()
      .transform((value) => {
        return (Number.isNaN(value) ? null : value);
      })
      .required('Введено некорректное значение'),
  };

  if (isSeries) {
    defaultValidation.point = yup
      .number()
      .transform((point) => (Number.isNaN(point) ? null : point))
      .min(0, 'Номер замера не может быть меньше нуля')
      .required('Введен некорректный номер замера');
  }

  return yup.object().shape(defaultValidation);
};

export default function Measurement({
  measurement,
  zone,
  index,
  onChangeValue,
  isSeries = false,
  existingPoints = [],
}: MeasurementProps) {
  const [update, { isLoading }] = useUpdateDataValueTsMutation();
  const [isShowHistory, setIsShowHistory] = React.useState(false);
  const [ts, setTS] = React.useState(measurement);
  const [errors, setErrors] = React.useState<string[]>([]);

  const toggleHistory = () => setIsShowHistory((cur) => !cur);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let fieldValue: unknown = value;
    if (name === 'dataValueComment') {
      fieldValue = value === '' ? null : { comment: value };
    }
    setTS((cur) => ({
      ...cur,
      [name]: fieldValue === '' ? null : fieldValue,
    }));
  };

  const save = async () => {
    let err = '';
    try {
      if (ts.isNew && isSeries && ts.point && existingPoints.includes(+ts.point)) {
        err = 'Введенный номер замера уже существует';
      }
      await validationSchema(isSeries).validate(ts, { abortEarly: false });
      if (err) {
        throw new yup.ValidationError(err);
      }
      await update(ts).unwrap();
      toaster.success('Сохранено');
      const { isNew, ...rest } = ts;
      onChangeValue(index, rest);
    } catch (e) {
      if (e instanceof yup.ValidationError) {
        setErrors(e.errors);
      }
    }
  };

  // при смене значения скрывает окно истории
  React.useEffect(() => {
    setIsShowHistory(false);
  }, [index]);

  React.useEffect(() => {
    if (measurement) setTS(measurement);
  }, [measurement]);

  return (
    <Pane display="grid" gridTemplateRows="1fr max-content" rowGap=".5rem">
      <CornerDialogError errors={errors} closeDialog={() => setErrors([])} />
      {
        isShowHistory
          ? <HistoryChanges measurement={measurement} zone={zone} />
          : <EditForm measurement={ts} onChange={onChange} isSeries={isSeries} existingPoints={existingPoints} />
      }
      <Pane display="grid" gridTemplateColumns="repeat(2, max-content)" columnGap=".5rem" justifyContent="flex-end">
        <Button
          onClick={toggleHistory}
          disabled={ts.isNew}
        >
          {isShowHistory ? 'Назад' : 'Показать историю'}
        </Button>
        {!isShowHistory && (
          <Button
            intent="success"
            appearance="primary"
            onClick={save}
            isLoading={isLoading}
          >
            Сохранить
          </Button>
        )}
      </Pane>
    </Pane>
  );
}
